import React, { createContext, useContext, useState, useEffect } from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  isCollapsed: boolean;
  toggleCollapsed: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem("runway-theme");
    return (savedTheme as Theme) || "dark";
  });

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedCollapsed = localStorage.getItem("runway-nav-collapsed");
    return savedCollapsed === "true";
  });

  useEffect(() => {
    localStorage.setItem("runway-theme", theme);
    document.documentElement.classList.toggle("light", theme === "light");
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("runway-nav-collapsed", isCollapsed.toString());
  }, [isCollapsed]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => !prev);
  };

  return (
    <ThemeContext.Provider
      value={{ theme, toggleTheme, isCollapsed, toggleCollapsed }}>
      {children}
    </ThemeContext.Provider>
  );
};
